export type ApiErrorDetails = {
  title: string;
  message: string;
};

export function getApiErrorDetails(
  err: unknown,
  fallbackTitle: string,
  fallbackMessage: string,
): ApiErrorDetails {
  const errObj = (err && typeof err === 'object' ? err : {}) as {
    error?: unknown;
    url?: unknown;
    message?: unknown;
    status?: unknown;
  };
  const errorPayload = errObj.error;
  const requestUrl = `${errObj.url ?? ''}`;
  let title = fallbackTitle;
  const detailedMessage = extractBestErrorMessage(errorPayload);

  if (detailedMessage) {
    return { title, message: detailedMessage };
  }

  if (typeof errorPayload === 'string') {
    const trimmed = errorPayload.trim();
    if (trimmed.length > 0) {
      return { title, message: trimmed };
    }
  }

  if (Array.isArray(errorPayload) && errorPayload.length > 0) {
    const first = errorPayload[0];
    if (typeof first === 'string' && first.trim()) {
      return { title, message: first.trim() };
    }
    if (first && typeof first === 'object') {
      const firstObjTitle = first.title ?? first.titulo ?? first.Title ?? '';
      if (typeof firstObjTitle === 'string' && firstObjTitle.trim()) {
        title = firstObjTitle.trim();
      }

      const firstObjMsg =
        first.message ??
        first.mensagem ??
        first.aviso ??
        first.ERRO ??
        first.erro ??
        first.Message ??
        first.error ??
        '';
      if (typeof firstObjMsg === 'string' && firstObjMsg.trim()) {
        return { title, message: firstObjMsg.trim() };
      }
    }
  }

  if (errorPayload && typeof errorPayload === 'object') {
    const errorObject = errorPayload as Record<string, unknown>;
    const objectTitle =
      errorObject['title'] ?? errorObject['titulo'] ?? errorObject['Title'] ?? '';
    if (typeof objectTitle === 'string' && objectTitle.trim()) {
      title = objectTitle.trim();
    }

    const objectMsg =
      errorObject['message'] ??
      errorObject['mensagem'] ??
      errorObject['aviso'] ??
      errorObject['ERRO'] ??
      errorObject['erro'] ??
      errorObject['Message'] ??
      errorObject['error'] ??
      '';
    if (typeof objectMsg === 'string' && objectMsg.trim()) {
      return { title, message: objectMsg.trim() };
    }
  }

  const errMessage =
    typeof errObj['message'] === 'string' ? errObj['message'] : '';
  const errStatus =
    typeof errObj['status'] === 'number' ? errObj['status'] : null;

  if (
    errMessage.trim() &&
    !errMessage.includes('Http failure response for')
  ) {
    return { title, message: errMessage.trim() };
  }

  if (
    errStatus === 404 &&
    requestUrl.includes('/frequencia') &&
    requestUrl.includes('tipo=ALUNOS')
  ) {
    return {
      title: 'Edição bloqueada',
      message: 'A edição desta frequência está bloqueada para esta disciplina.',
    };
  }

  return { title, message: fallbackMessage };
}

export const DEFAULT_API_ERROR_TITLE = 'Oops... Algo deu errado!';

export function isGenericApiErrorMessage(message: string): boolean {
  return /^erro na execu[cç][aã]o do comando$/i.test(message.trim());
}

export function getApiErrorUserMessage(rawMessage: unknown): string {
  return parseApiErrorMessage(rawMessage).userMessage;
}

export function buildStandardApiErrorSwalOptions(
  rawMessage: string,
): Record<string, unknown> {
  const swalOptions: Record<string, unknown> = {
    icon: 'error',
    title: DEFAULT_API_ERROR_TITLE,
    customClass: { confirmButton: 'btn' },
  };

  if (rawMessage.trim() && !isGenericApiErrorMessage(rawMessage)) {
    swalOptions['html'] = buildTechnicalErrorHtml(rawMessage);
  }

  return swalOptions;
}

export function buildTechnicalErrorHtml(rawMessage: unknown): string {
  const { userMessage, technicalDetails } = parseApiErrorMessage(rawMessage);
  const userMessageHtml = escapeHtmlWithBreaks(userMessage);

  if (!technicalDetails) {
    return userMessageHtml;
  }

  return `${userMessageHtml}
    <div class="accordion accordion-flush mt-3" id="apiErrorTechnicalAccordion">
      <div class="accordion-item">
        <h2 class="accordion-header" id="apiErrorTechnicalHeading">
          <button
            class="accordion-button collapsed text-muted d-flex gap-2"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#apiErrorTechnicalCollapse"
            aria-expanded="false"
            aria-controls="apiErrorTechnicalCollapse"
          >
            <span class="material-symbols-outlined">privacy_tip</span>
            <span>Informações técnicas</span>
          </button>
        </h2>
        <div
          id="apiErrorTechnicalCollapse"
          class="accordion-collapse collapse"
          aria-labelledby="apiErrorTechnicalHeading"
          data-bs-parent="#apiErrorTechnicalAccordion"
        >
          <div class="accordion-body text-start">
            ${escapeHtmlWithBreaks(technicalDetails)}
          </div>
        </div>
      </div>
    </div>`;
}

function extractBestErrorMessage(payload: unknown): string | null {
  const technicalInfoRegex = /(\/[A-Z_]+(?:\?|%3F)[\s\S]*)/i;
  const genericErrorRegex = /^erro na execu[cç][aã]o do comando$/i;
  const candidates: string[] = [];

  const addCandidate = (value: unknown) => {
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (normalized) candidates.push(normalized);
    }
  };

  if (typeof payload === 'string') {
    addCandidate(payload);
  } else if (Array.isArray(payload)) {
    payload.forEach((item) => {
      if (typeof item === 'string') {
        addCandidate(item);
        return;
      }
      if (item && typeof item === 'object') {
        const objectItem = item as Record<string, unknown>;
        addCandidate(objectItem['error']);
        addCandidate(objectItem['message']);
        addCandidate(objectItem['mensagem']);
        addCandidate(objectItem['aviso']);
        addCandidate(objectItem['ERRO']);
        addCandidate(objectItem['erro']);
        addCandidate(objectItem['title']);
        addCandidate(objectItem['titulo']);
        addCandidate(objectItem['Message']);
        addCandidate(objectItem['Title']);
      }
    });
  } else if (payload && typeof payload === 'object') {
    const objectPayload = payload as Record<string, unknown>;
    addCandidate(objectPayload['error']);
    addCandidate(objectPayload['message']);
    addCandidate(objectPayload['mensagem']);
    addCandidate(objectPayload['aviso']);
    addCandidate(objectPayload['ERRO']);
    addCandidate(objectPayload['erro']);
    addCandidate(objectPayload['title']);
    addCandidate(objectPayload['titulo']);
    addCandidate(objectPayload['Message']);
    addCandidate(objectPayload['Title']);
  }

  if (candidates.length === 0) {
    return null;
  }

  let best = candidates[0];
  let bestScore = Number.NEGATIVE_INFINITY;

  for (const candidate of candidates) {
    let score = candidate.length;
    if (technicalInfoRegex.test(candidate)) score += 1000;
    if (genericErrorRegex.test(candidate)) score -= 400;
    if (score > bestScore) {
      best = candidate;
      bestScore = score;
    }
  }

  return best;
}

function normalizeApiLineBreaks(rawMessage: unknown): string {
  if (typeof rawMessage !== 'string') {
    return '';
  }

  return rawMessage
    .replace(/\r\n?/g, '\n')
    .replace(/&amp;lt;\s*br\s*\/?\s*&amp;gt;/gi, '\n')
    .replace(/&lt;\s*br\s*\/?\s*&gt;/gi, '\n')
    .replace(/&#60;\s*br\s*\/?\s*&#62;/gi, '\n')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .trim();
}

function parseApiErrorMessage(rawMessage: unknown): {
  userMessage: string;
  technicalDetails: string;
} {
  const message = normalizeApiLineBreaks(rawMessage);
  if (!message) {
    return {
      userMessage: 'Não foi possível concluir a operação.',
      technicalDetails: '',
    };
  }

  const decoded = decodeHtmlEntities(message);
  const text = normalizeWhitespace(htmlToPlainText(decoded));
  const technicalFromHtml = extractTechnicalDetailsFromHtml(decoded);
  const technicalFromText = extractTechnicalDetailsFromText(text);
  const technicalDetails = technicalFromHtml || technicalFromText;
  const userMessage = normalizeWhitespace(
    removeIconArtifactText(removeTechnicalSection(text)),
  );

  return {
    userMessage: userMessage || 'Não foi possível concluir a operação.',
    technicalDetails,
  };
}

function extractTechnicalDetailsFromHtml(input: string): string {
  const patterns = [
    /<div[^>]*class=["'][^"']*accordion-body[^"']*["'][^>]*>([\s\S]*?)<\/div>/i,
    /<div[^>]*class=["'][^"']*swal-technical-content[^"']*["'][^>]*>([\s\S]*?)<\/div>/i,
  ];

  for (const pattern of patterns) {
    const match = input.match(pattern);
    if (!match?.[1]) {
      continue;
    }
    const value = normalizeWhitespace(htmlToPlainText(decodeHtmlEntities(match[1])));
    if (value) {
      return value;
    }
  }

  return '';
}

function extractTechnicalDetailsFromText(input: string): string {
  const infoLabelMatch = input.match(
    /informa[cç][oõ]es\s+t[eé]cnicas?\s*:?\s*([\s\S]*)$/i,
  );
  const infoDetails = normalizeWhitespace(infoLabelMatch?.[1] ?? '');
  if (infoDetails) {
    return infoDetails;
  }

  const endpointMatch = input.match(/(\/[A-Z_]+(?:\?|%3F)[\s\S]*)/i);
  return normalizeWhitespace(endpointMatch?.[1] ?? '');
}

function removeTechnicalSection(input: string): string {
  let output = input;
  output = output.replace(
    /\n?\s*informa[cç][oõ]es\s+t[eé]cnicas?\s*:?\s*[\s\S]*$/i,
    '',
  );

  const endpointMatch = output.match(/(\/[A-Z_]+(?:\?|%3F)[\s\S]*)/i);
  if (endpointMatch?.index !== undefined) {
    output = output.slice(0, endpointMatch.index);
  }

  return normalizeWhitespace(output);
}

function removeIconArtifactText(input: string): string {
  return input
    .replace(/\bprivacy_tip\b/gi, '')
    .replace(/\bexpand_less\b/gi, '')
    .replace(/\bexpand_more\b/gi, '');
}

function htmlToPlainText(input: string): string {
  return input
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(div|p|li|h1|h2|h3|h4|h5|h6)\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '- ')
    .replace(/<[^>]+>/g, ' ');
}

function decodeHtmlEntities(input: string): string {
  if (typeof document !== 'undefined') {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = input;
    return textArea.value;
  }

  return input
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

function normalizeWhitespace(input: string): string {
  return input
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function escapeHtmlWithBreaks(text: string): string {
  return escapeHtml(text).replace(/\r?\n/g, '<br>');
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
